/**
 * CLOSDD SEO layer.
 *
 * Everything here is generated from data the candidate has already entered.
 * Candidates are never asked to write SEO copy, and nothing is invented:
 * every sentence below is assembled only from fields that are actually filled in.
 */

import { splitList, type ShowcaseProfile } from "./showcase";

export const SITE_URL = "https://www.closdd.com";
export const SITE_NAME = "CLOSDD";

/* ------------------------------------------------------------------ *
 * Taxonomy used by the footer, the internal links and category pages
 * ------------------------------------------------------------------ */

export type JobCategory = {
  slug: string;
  /** Route path — typed literal so <Link to> stays type-safe. */
  path: string;
  label: string;
  plural: string;
  blurb: string;
  /** Words that, if present in a candidate/job title, mean this category fits. */
  match: string[];
};

export const jobCategories = [
  {
    slug: "sales-jobs",
    path: "/sales-jobs",
    label: "Sales",
    plural: "Sales jobs",
    blurb:
      "Sales roles across the UK, from first sales job to senior closing roles. Every candidate applies with a voice pitch, so you hear how they sell before you interview.",
    match: ["sales"],
  },
  {
    slug: "account-executive-jobs",
    path: "/account-executive-jobs",
    label: "Account Executive",
    plural: "Account Executive jobs",
    blurb:
      "Full-cycle Account Executive roles selling into SMB, mid-market and enterprise buyers. Ideal for salespeople who source, run and close their own pipeline.",
    match: ["account executive", "ae"],
  },
  {
    slug: "sales-executive-jobs",
    path: "/sales-executive-jobs",
    label: "Sales Executive",
    plural: "Sales Executive jobs",
    blurb:
      "Sales Executive roles working inbound enquiries and self-generated outbound, usually with a base salary plus uncapped commission.",
    match: ["sales executive"],
  },
  {
    slug: "business-development-jobs",
    path: "/business-development-jobs",
    label: "Business Development",
    plural: "Business Development jobs",
    blurb:
      "Business Development Manager and Business Development Executive roles focused on opening new accounts and building territory from scratch.",
    match: ["business development", "bdm"],
  },
  {
    slug: "sales-manager-jobs",
    path: "/sales-manager-jobs",
    label: "Sales Manager",
    plural: "Sales Manager jobs",
    blurb:
      "Sales Manager and Head of Sales roles leading teams, coaching pipeline and owning a number across the whole desk.",
    match: ["sales manager", "head of sales", "sales director"],
  },
  {
    slug: "sdr-jobs",
    path: "/sdr-jobs",
    label: "SDR",
    plural: "SDR jobs",
    blurb:
      "Sales Development Representative roles booking qualified meetings through outbound calls, email and social selling.",
    match: ["sdr", "sales development"],
  },
  {
    slug: "bdr-jobs",
    path: "/bdr-jobs",
    label: "BDR",
    plural: "BDR jobs",
    blurb:
      "Business Development Representative roles generating pipeline for closers, usually the first step into a full-cycle sales career.",
    match: ["bdr", "business development representative"],
  },
  {
    slug: "telesales-jobs",
    path: "/telesales-jobs",
    label: "Telesales",
    plural: "Telesales jobs",
    blurb:
      "Telesales and appointment setting roles where the phone is the job. High volume, fast feedback and commission that reflects performance.",
    match: ["telesales", "appointment setter", "setter", "call centre"],
  },
] as const satisfies readonly JobCategory[];

export type Category = (typeof jobCategories)[number];

export const salesLocations: { slug: string; name: string }[] = [
  { slug: "london", name: "London" },
  { slug: "manchester", name: "Manchester" },
  { slug: "birmingham", name: "Birmingham" },
  { slug: "leeds", name: "Leeds" },
  { slug: "liverpool", name: "Liverpool" },
  { slug: "remote-uk", name: "Remote UK" },
];

export const salesIndustries = [
  "SaaS",
  "Technology",
  "Recruitment",
  "Finance",
  "Insurance",
  "Real Estate",
  "Fundraising",
  "E-commerce",
  "B2B",
  "B2C",
  "High-Ticket Sales",
];

export function categoryBySlug(slug: string) {
  return jobCategories.find((c) => c.slug === slug);
}

export function locationBySlug(slug: string) {
  return salesLocations.find((l) => l.slug === slug);
}

/** Categories that genuinely relate to this candidate's role, for internal linking. */
export function categoriesForProfile(role: string, targetRoles: string[] = []): Category[] {
  const haystack = [role, ...targetRoles].join(" ").toLowerCase();
  const hits = jobCategories.filter(
    (c) => c.slug !== "sales-jobs" && c.match.some((m) => haystack.includes(m)),
  );
  const base = jobCategories[0]!;
  return hits.length ? [base, ...hits].slice(0, 5) : [base];
}

/* ------------------------------------------------------------------ *
 * Slugs
 * ------------------------------------------------------------------ */

export function baseSlug(name: string) {
  return (
    name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .trim()
      .replace(/\s+/g, "-") || "salesperson"
  );
}

/** jordan-whitfield, then jordan-whitfield-2 when the name is already taken. */
export function uniqueSlug(name: string, taken: string[]) {
  const base = baseSlug(name);
  if (!taken.includes(base)) return base;
  let n = 2;
  while (taken.includes(`${base}-${n}`)) n += 1;
  return `${base}-${n}`;
}

export function profileUrl(slug: string) {
  return `${SITE_URL}/profile/${slug}`;
}

/* ------------------------------------------------------------------ *
 * Derived, human-readable facts
 * ------------------------------------------------------------------ */

function city(location: string) {
  return location.split(",")[0]?.trim() ?? "";
}

function listSentence(items: string[]) {
  const clean = items.filter(Boolean);
  if (clean.length <= 1) return clean[0] ?? "";
  return `${clean.slice(0, -1).join(", ")} and ${clean[clean.length - 1]}`;
}

export type ProfileSeo = {
  name: string;
  role: string;
  location: string;
  title: string;
  description: string;
  /** 1–3 natural sentences unique to this candidate. */
  paragraph: string;
  /** Short factual bullets, only for fields that exist. */
  facts: { label: string; value: string }[];
  industries: string[];
  skills: string[];
  categories: Category[];
};

export function buildProfileSeo(name: string, p: ShowcaseProfile): ProfileSeo {
  const displayName = (name || "").trim() || "Sales professional";
  const first = displayName.split(" ")[0] || displayName;
  const role = (p.targetRole || "").trim();
  const location = (p.location || "").trim();
  const town = city(location);
  const industries = splitList(p.industries);
  const skills = (p.skillTags || []).filter(Boolean);
  const years = (p.yearsSelling || "").trim();
  const targets = (p.targetRoles || []).filter(Boolean);
  const environment = (p.environment || []).filter(Boolean);

  /* ---- title ---- */
  const titleParts = [displayName, role, town].filter(Boolean);
  const title = `${titleParts.join(" | ")} | ${SITE_NAME}`;

  /* ---- paragraph ---- */
  const sentences: string[] = [];

  const opener = [
    displayName,
    "is a",
    role ? role.toLowerCase().includes("sales") ? role : `${role}` : "sales professional",
    town ? `based in ${town}` : "",
    years ? `with ${years} ${Number(years) === 1 ? "year" : "years"} of sales experience` : "",
    industries.length ? `across ${listSentence(industries)}` : "",
  ]
    .filter(Boolean)
    .join(" ");
  sentences.push(`${opener}.`);

  if (skills.length || environment.length) {
    const strengths = listSentence([...skills.slice(0, 4), ...environment.slice(0, 2)].map((s) => s.toLowerCase()));
    if (strengths) sentences.push(`${first}'s experience covers ${strengths}.`);
  }

  const deal = (p.biggestDeal || p.avgDealSize || "").trim();
  if (deal) sentences.push(`Deal experience on this profile goes up to ${deal}.`);

  if (targets.length) {
    const where = p.locationPref
      ? p.locationPref.toLowerCase() === "remote"
        ? "remote roles"
        : `${p.locationPref.toLowerCase()} roles`
      : town
        ? `roles in and around ${town}`
        : "sales roles";
    const types = (p.employmentTypes || []).filter(Boolean);
    sentences.push(
      `${first} is currently open to ${listSentence(targets)} ${where}${
        types.length ? ` on a ${listSentence(types.map((t) => t.toLowerCase()))} basis` : ""
      }.`,
    );
  }

  const paragraph = sentences.join(" ");

  /* ---- meta description ---- */
  const descBits = [
    `View ${displayName}'s ${SITE_NAME} sales profile.`,
    [role || "Sales professional", years ? `with ${years} years of sales experience` : "", town ? `in ${town}` : ""]
      .filter(Boolean)
      .join(" ") + ".",
    industries.length ? `Industries: ${industries.slice(0, 3).join(", ")}.` : "",
    "Voice introduction and sales assessment included.",
  ].filter(Boolean);
  const description = descBits.join(" ").slice(0, 300);

  const facts = [
    { label: "Sales role", value: role },
    { label: "Location", value: location },
    { label: "Experience", value: years ? `${years} years in sales` : "" },
    { label: "Industries", value: industries.join(", ") },
    { label: "Works", value: [p.locationPref, ...(p.employmentTypes || [])].filter(Boolean).join(", ") },
    { label: "Availability", value: p.availabilityWhen || p.availability },
  ].filter((f) => Boolean(f.value));

  return {
    name: displayName,
    role,
    location,
    title,
    description,
    paragraph,
    facts,
    industries,
    skills,
    categories: categoriesForProfile(role, targets),
  };
}

/** Schema.org Person — public fields only, no ratings or verification claims. */
export function personJsonLd(seo: ProfileSeo, slug: string, p: ShowcaseProfile) {
  const data: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: seo.name,
    url: profileUrl(slug),
    description: seo.paragraph,
  };
  if (seo.role) data["jobTitle"] = seo.role;
  if (seo.location) data["address"] = { "@type": "PostalAddress", addressLocality: city(seo.location) };
  if (seo.skills.length) data["knowsAbout"] = seo.skills;
  if (p.linkedin) data["sameAs"] = [p.linkedin];
  return data;
}

export function isPubliclyIndexable(p: ShowcaseProfile) {
  return Boolean(p.enabled) && p.visibility === "public";
}

/* ------------------------------------------------------------------ *
 * Local record of slugs already in use, so names never collide.
 * ------------------------------------------------------------------ */

const SLUGS_KEY = "closdd.profile.slugs";

export function takenSlugs(): string[] {
  if (typeof window === "undefined") return [];
  try {
    return JSON.parse(window.localStorage.getItem(SLUGS_KEY) ?? "[]") as string[];
  } catch {
    return [];
  }
}

export function rememberSlug(slug: string) {
  if (typeof window === "undefined" || !slug) return;
  const all = new Set(takenSlugs());
  all.add(slug);
  window.localStorage.setItem(SLUGS_KEY, JSON.stringify([...all]));
}
